import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, ThumbsUp, ThumbsDown, HelpCircle, CheckCircle2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Public read-only candidate brief for hiring managers. No login required —
 * the token in the URL is the access credential. Includes a 1-click thumbs-up
 * / thumbs-down + comment that routes back to the recruiter via Resend.
 */
export default function BriefShare() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [decision, setDecision] = useState(null);
  const [comment, setComment] = useState("");

  useEffect(() => {
    axios
      .get(`${BACKEND}/api/brief-share/${token}`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("This brief link is invalid or has expired"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (choice) => {
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND}/api/brief-share/${token}/respond`, {
        decision: choice,
        comment,
      });
      setDecision(choice);
      toast.success("Thanks — your recruiter has been notified.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not record your response"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-blue-700" />
      </main>
    );
  }
  if (!data) {
    return (
      <main
        data-testid="brief-share-invalid"
        className="flex min-h-screen items-center justify-center bg-slate-50 p-6"
      >
        <p className="text-sm text-slate-600">
          This brief link is invalid or has expired.
        </p>
      </main>
    );
  }
  const { share, interview } = data;
  const job = interview.job || {};
  const alreadyAnswered = !!share.decision;
  const finalDecision = decision || share.decision;

  return (
    <main
      data-testid="brief-share-page"
      className="min-h-screen bg-slate-50 px-4 py-10"
    >
      <div className="mx-auto max-w-3xl rounded-sm border border-slate-200 bg-white p-8 shadow-sm">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-blue-700">
          Candidate brief · for hiring manager
        </p>
        <h1
          data-testid="brief-share-candidate-name"
          className="mt-2 font-heading text-3xl font-semibold text-slate-900 sm:text-4xl"
        >
          {share.candidate_name}
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          For the <strong>{share.job_title}</strong> role · sent by{" "}
          {share.recruiter_name || share.recruiter_email}
        </p>

        {share.note && (
          <p className="mt-4 border-l-2 border-slate-300 pl-3 text-sm italic text-slate-700">
            {share.note}
          </p>
        )}

        {/* Overall score */}
        <div className="mt-8 border border-slate-200 p-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-500">
            Overall score
          </p>
          <p className="mt-1 text-3xl font-semibold text-blue-700">
            {interview.overall_score ?? "—"}
            <span className="text-base text-slate-400">/10</span>
          </p>
        </div>

        {/* Per-question */}
        <section className="mt-8 space-y-4">
          {(interview.answers || []).map((a, i) => {
            const q = (job.questions || []).find(
              (qq) => qq.id === a.question_id
            );
            return (
              <div
                key={a.question_id || i}
                data-testid={`brief-share-q-${i}`}
                className="border border-slate-200 p-4"
              >
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                  Q{i + 1} · score{" "}
                  <span className="text-slate-900">
                    {a.score?.toFixed?.(1) ?? a.score ?? "—"}/10
                  </span>
                </p>
                <p className="mt-1 text-sm font-medium text-slate-900">
                  {a.question_text}
                </p>
                {q?.listen_for && (
                  <p className="mt-2 border-l-2 border-amber-300 pl-3 text-xs italic text-slate-700">
                    <strong className="not-italic text-amber-700">
                      Listen for:
                    </strong>{" "}
                    {q.listen_for}
                  </p>
                )}
                {a.rubric_check?.signals?.length > 0 && (
                  <ul className="mt-3 space-y-1">
                    {a.rubric_check.signals.map((s, idx) => {
                      const palette =
                        s.status === "hit"
                          ? "text-emerald-700"
                          : s.status === "partial"
                          ? "text-amber-700"
                          : "text-red-700";
                      const icon =
                        s.status === "hit"
                          ? "✓"
                          : s.status === "miss"
                          ? "✗"
                          : "~";
                      return (
                        <li
                          key={idx}
                          className={`flex items-start gap-2 text-xs ${palette}`}
                        >
                          <strong>{icon}</strong>
                          <span className="text-slate-800">{s.label}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {a.transcript && (
                  <details className="mt-3 text-xs text-slate-600">
                    <summary className="cursor-pointer text-slate-500 hover:text-slate-900">
                      Show transcript
                    </summary>
                    <p className="mt-2 whitespace-pre-wrap text-slate-700">
                      {a.transcript}
                    </p>
                  </details>
                )}
              </div>
            );
          })}
        </section>

        {/* Decision panel */}
        <section
          data-testid="brief-share-decision-panel"
          className="mt-10 border-t border-slate-200 pt-8"
        >
          {alreadyAnswered || finalDecision ? (
            <div
              data-testid="brief-share-thanks"
              className="flex items-center gap-3 rounded-sm border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800"
            >
              <CheckCircle2 className="h-5 w-5 shrink-0" />
              <div>
                Thanks — your verdict (<strong>{finalDecision}</strong>) was
                recorded. The recruiter has been notified.
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm font-semibold text-slate-900">
                Your call?
              </p>
              <textarea
                data-testid="brief-share-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Optional 2-line comment (e.g. 'Strong on rollback, light on metrics — move to onsite.')"
                rows={3}
                maxLength={600}
                className="mt-3 w-full rounded-sm border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              />
              <div className="mt-4 grid gap-2 sm:grid-cols-3">
                <Button
                  data-testid="brief-share-decision-yes"
                  disabled={submitting}
                  onClick={() => submit("yes")}
                  className="rounded-sm bg-emerald-600 py-5 text-white hover:bg-emerald-500"
                >
                  <ThumbsUp className="mr-2 h-4 w-4" />
                  Yes — move forward
                </Button>
                <Button
                  data-testid="brief-share-decision-maybe"
                  disabled={submitting}
                  onClick={() => submit("maybe")}
                  variant="outline"
                  className="rounded-sm border-amber-300 py-5 text-amber-700 hover:bg-amber-50"
                >
                  <HelpCircle className="mr-2 h-4 w-4" />
                  Maybe
                </Button>
                <Button
                  data-testid="brief-share-decision-no"
                  disabled={submitting}
                  onClick={() => submit("no")}
                  variant="outline"
                  className="rounded-sm border-red-300 py-5 text-red-700 hover:bg-red-50"
                >
                  <ThumbsDown className="mr-2 h-4 w-4" />
                  No — pass
                </Button>
              </div>
            </>
          )}
        </section>
      </div>
    </main>
  );
}
